import { Fragment, useState } from 'react';
import { idError } from '../lib/country.ts';
import { addressLine, businessRules, buyerAddress, buyerIdLabel, ownIdLines, ownTaxLabel } from '../lib/taxes.ts';
import { createPortal } from 'react-dom';
import { Icon } from '../components/Icon.tsx';
import { Button, Dialog, EmptyState, Field, IconButton, Skeleton, StatusBadge } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action } from '../lib/api.ts';
import { day, todayIso } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { parseNumber, qtyText, type Unit } from './Invoices.tsx';
import { quoteName, ReasonBox } from './Quotes.tsx';
import { useAction, useSave } from './manage/common.tsx';

type State = 'draft' | 'done' | 'cancelled';

export interface DeliveryRow {
  id: string;
  number: number | null;
  year: number | null;
  state: State;
  date: string;
  customer: string;
  customer_name: string;
  order: string;
  order_number: number;
  order_year: number;
  reference: string | null;
  carrier: string | null;
  vehicle: string | null;
  lines: number;
}
interface DeliveryDetail {
  delivery: {
    id: string;
    number: number | null;
    year: number | null;
    name: string | null;
    state: State;
    order: string;
    customer: string;
    date: string;
    location: string;
    location_name: string;
    address: string | null;
    town: string | null;
    vehicle_owner: 'own' | 'carrier';
    carrier: string | null;
    carrier_tax_id: string | null;
    vehicle: string | null;
    note: string | null;
    done_at: string | null;
    done_by_name: string | null;
    cancel_reason: string | null;
  };
  order: { id: string; number: number; year: number; reference: string | null; state: string };
  lines: { id: string; order_line: string; name: string; unit: Unit; qty: number; ordered: number; other: number }[];
  customer: { id: string; name: string; tax_id: string | null; id_type: string | null; address: string | null; town: string | null; country: string; phone: string | null };
  company: { legal_name: string; trade_name: string | null; tax_id: string | null; address: string | null; city: string | null; country: string; phone: string | null; email: string | null };
  branch: { name: string; address: string | null; city: string | null };
  /** The fiscal delivery note (WTN), when fiscalization is on. */
  fiscal: Wtn | null;
}
interface Wtn {
  test: boolean;
  state: 'pending' | 'fiscalized' | 'rejected';
  last_error: string | null;
  wtn_num: string;
  issued_at: string;
  wtnic: string;
  fwtnic: string | null;
  operator: string;
  business_unit: string;
  software: string;
}
interface ToDeliver {
  order_line: string;
  name: string;
  unit: Unit;
  ordered: number;
  delivered: number;
  left: number;
}

/** "DN 3/2026", or "Draft delivery note" before the goods left. */
export const deliveryName = (d: { number: number | null; year: number | null }) => (d.number ? `${t('dn.prefix')} ${d.number}/${d.year}` : t('dn.draft_name'));
const unitText = (u: Unit) => t(`inv.u_${u}` as MessageKey);

export function DeliveryBadge({ state }: { state: State }) {
  if (state === 'draft') return <StatusBadge tone="warn" icon="edit" label={t('dn.st_draft')} />;
  if (state === 'done') return <StatusBadge tone="ok" icon="check" label={t('dn.st_done')} />;
  return <StatusBadge tone="neutral" icon="x" label={t('dn.st_cancelled')} />;
}

const FILTERS = ['all', 'draft', 'done'] as const;

/** Every delivery note, drafts first. */
export function DeliveryList({ onOpen }: { onOpen: (id: string) => void }) {
  const [filter, setFilter] = useState<(typeof FILTERS)[number]>('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const { data } = useAction<DeliveryRow[]>('invoicing.deliveries', { filter, search: q || undefined });
  return (
    <>
      <div className="toolbar">
        <div className="segmented" role="radiogroup" aria-label={t('inv.show')}>
          {FILTERS.map((f) => (
            <button key={f} role="radio" aria-checked={filter === f} className={filter === f ? 'active' : ''} onClick={() => setFilter(f)}>
              {t(`dn.f_${f}`)}
            </button>
          ))}
        </div>
        <form
          className="search"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            setQ(search.trim());
          }}
        >
          <Icon name="search" size={18} />
          <input className="input" placeholder={t('dn.search')} aria-label={t('dn.search')} value={search} onChange={(e) => setSearch(e.target.value)} onBlur={() => setQ(search.trim())} />
          {search ? <IconButton icon="x" label={t('common.clear')} onClick={() => (setSearch(''), setQ(''))} /> : null}
        </form>
      </div>
      <section className="list-card" aria-label={t('inv.tab_deliveries')}>
        {!data ? (
          <Skeleton h={160} />
        ) : data.length === 0 ? (
          <EmptyState icon="truck" title={q ? t('order.no_results') : t('dn.none')} body={q ? undefined : t('dn.none_body')} />
        ) : (
          data.map((d) => (
            <button key={d.id} className="list-row stock-row" onClick={() => onOpen(d.id)}>
              <span className="list-icon" aria-hidden="true">
                <Icon name="truck" size={18} />
              </span>
              <span className="list-main">
                <span className="list-title">
                  {deliveryName(d)} · {d.customer_name}
                </span>
                <span className="list-sub">
                  {[day(d.date), quoteName({ number: d.order_number, year: d.order_year, order: true }), d.vehicle, d.reference].filter(Boolean).join(' · ')}
                </span>
              </span>
              <span className="stock-qty">
                <span className="muted">{tn('dn.n_lines', d.lines)}</span>
              </span>
              <DeliveryBadge state={d.state} />
            </button>
          ))
        )}
      </section>
    </>
  );
}

/** One delivery note: as it prints, and the goods leaving. */
export function DeliveryDialog({ id, onClose, onOpenQuote, onChange }: { id: string; onClose: () => void; onOpenQuote: (id: string) => void; onChange: () => Promise<void> }) {
  const { data: d, reload } = useAction<DeliveryDetail>('invoicing.delivery', { delivery: id });
  const [editing, setEditing] = useState(false);
  const refresh = async () => {
    await Promise.all([reload(), onChange()]);
  };
  if (!d) {
    return (
      <Dialog title={t('dn.title')} onClose={onClose} size="lg">
        <Skeleton h={320} />
      </Dialog>
    );
  }
  if (editing) return <DeliveryEditor order={d.order.id} detail={d} onClose={() => setEditing(false)} onSaved={async () => (setEditing(false), await refresh())} />;
  return <DeliveryView d={d} onClose={onClose} onEdit={() => setEditing(true)} onOpenQuote={onOpenQuote} onChange={refresh} />;
}

function DeliveryView({ d, onClose, onEdit, onOpenQuote, onChange }: { d: DeliveryDetail; onClose: () => void; onEdit: () => void; onOpenQuote: (id: string) => void; onChange: () => Promise<void> }) {
  const { can } = useSession();
  const { busy, run } = useSave();
  const [cancelling, setCancelling] = useState(false);
  const [printing, setPrinting] = useState(false);
  const n = d.delivery;
  const manage = can('invoicing.manage');
  const print = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 50);
  };
  const step = (name: string, done: string, input: Record<string, unknown> = {}) =>
    void run(async () => {
      await action(name, { delivery: n.id, ...input });
      await onChange();
    }, done);
  return (
    <Dialog
      title={`${deliveryName(n)} · ${d.customer.name}`}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button icon="printer" onClick={print}>
              {t('inv.print')}
            </Button>
            {manage && n.state === 'draft' ? (
              <Button icon="edit" onClick={onEdit}>
                {t('dn.edit')}
              </Button>
            ) : null}
            {manage && n.state === 'draft' ? (
              <Button variant="ghost" icon="x" onClick={() => setCancelling(true)}>
                {t('dn.cancel')}
              </Button>
            ) : null}
          </span>
          {manage && n.state === 'draft' ? (
            <Button variant="primary" icon="truck" onClick={() => step('invoicing.deliver', t('dn.delivered'))} busy={busy}>
              {t('dn.deliver')}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="form-stack">
        <div className="item-summary">
          <div>
            <span className="muted">{t('dn.order')}</span>
            <button className="link-button" onClick={() => onOpenQuote(d.order.id)}>
              {quoteName({ ...d.order, order: true })}
            </button>
          </div>
          <div>
            <span className="muted">{t('quote.date')}</span>
            <strong>{day(n.date)}</strong>
          </div>
          {n.vehicle ? (
            <div>
              <span className="muted">{t('dn.vehicle')}</span>
              <strong className="mono">{n.vehicle}</strong>
            </div>
          ) : null}
          <DeliveryBadge state={n.state} />
        </div>
        {n.state === 'draft' ? (
          <p className="callout callout-info">
            <Icon name="box" size={16} />
            <span>{t('dn.draft_note')}</span>
          </p>
        ) : null}
        {n.state === 'done' && n.done_by_name ? (
          <p className="callout callout-ok">
            <Icon name="check" size={16} />
            <span>{t('dn.done_note', { name: n.done_by_name })}</span>
          </p>
        ) : null}
        {d.fiscal ? <WtnBox f={d.fiscal} /> : null}
        {n.state === 'cancelled' && n.cancel_reason ? (
          <p className="callout callout-info">
            <Icon name="note" size={16} />
            <span>{t('quote.cancelled_because', { reason: n.cancel_reason })}</span>
          </p>
        ) : null}
        {cancelling ? (
          <ReasonBox title={t('dn.cancel_title')} optional button={t('dn.cancel')} onCancel={() => setCancelling(false)} onDone={(reason) => step('invoicing.cancel_delivery', t('dn.cancelled'), { reason })} />
        ) : null}
        <div className="inv-preview">
          <DeliveryPaper d={d} />
        </div>
      </div>
      {printing
        ? createPortal(
            <div className="print-sheet print-invoice">
              <DeliveryPaper d={d} />
            </div>,
            document.body,
          )
        : null}
    </Dialog>
  );
}

/** A delivery note for what is left on an order, or a draft being changed. */
export function DeliveryEditor({ order, detail, onClose, onSaved }: { order: string; detail?: DeliveryDetail; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { data } = useAction<ToDeliver[]>('invoicing.order_to_deliver', { order });
  if (!data) {
    return (
      <Dialog title={detail ? deliveryName(detail.delivery) : t('dn.new')} onClose={onClose} size="lg">
        <Skeleton h={240} />
      </Dialog>
    );
  }
  return <DeliveryForm order={order} detail={detail} lines={data} onClose={onClose} onSaved={onSaved} />;
}

function DeliveryForm({ order, detail, lines, onClose, onSaved }: { order: string; detail?: DeliveryDetail; lines: ToDeliver[]; onClose: () => void; onSaved: (id: string) => Promise<void> }) {
  const { busy, run } = useSave();
  const n = detail?.delivery;
  const open = lines.filter((l) => l.left > 0);
  const [date, setDate] = useState(n?.date ?? todayIso());
  const [address, setAddress] = useState(n?.address ?? '');
  const [town, setTown] = useState(n?.town ?? '');
  const [owner, setOwner] = useState<'own' | 'carrier'>(n?.vehicle_owner ?? 'own');
  const [carrier, setCarrier] = useState(n?.carrier ?? '');
  const [carrierId, setCarrierId] = useState(n?.carrier_tax_id ?? '');
  const [vehicle, setVehicle] = useState(n?.vehicle ?? '');
  const [note, setNote] = useState(n?.note ?? '');
  const [qty, setQty] = useState<Record<string, string>>(
    Object.fromEntries(open.map((l) => [l.order_line, detail ? qtyText(detail.lines.find((x) => x.order_line === l.order_line)?.qty ?? 0) : qtyText(l.left)])),
  );
  const picked = open.map((l) => ({ l, v: qty[l.order_line]?.trim() ? parseNumber(qty[l.order_line]!) : 0 }));
  const bad = picked.some(({ l, v }) => v === null || v < 0 || v > l.left + 1e-9);
  const niptBad = owner === 'carrier' && !!idError(businessRules()?.taxId, carrierId);
  const chosen = picked.filter(({ v }) => (v ?? 0) > 0).map(({ l, v }) => ({ order_line: l.order_line, qty: v! }));
  const save = () =>
    void run(async () => {
      const saved = await action<{ id: string }>('invoicing.save_delivery', {
        delivery: n?.id,
        order,
        date,
        address: address.trim() || null,
        town: town.trim() || null,
        vehicle_owner: owner,
        carrier: carrier.trim() || null,
        carrier_tax_id: owner === 'carrier' ? carrierId.trim() || null : null,
        vehicle: vehicle.trim() || null,
        note: note.trim() || null,
        lines: chosen,
      });
      await onSaved(saved.id);
    }, t('dn.saved'));
  return (
    <Dialog
      title={n ? deliveryName(n) : t('dn.new')}
      onClose={onClose}
      size="lg"
      footer={
        <>
          <span className="foot-actions">
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
          </span>
          <Button variant="primary" icon="check" disabled={bad || niptBad || !chosen.length} busy={busy} onClick={save}>
            {t('dn.save_draft')}
          </Button>
        </>
      }
    >
      <div className="form-stack">
        {!open.length ? (
          <p className="callout callout-ok">
            <Icon name="check" size={16} />
            <span>{t('dn.all_delivered')}</span>
          </p>
        ) : (
          <section className="item-moves">
            <h3>{t('dn.what_goes')}</h3>
            <p className="muted">{t('dn.what_goes_hint')}</p>
            <div className="list-card">
              {picked.map(({ l, v }) => (
                <div key={l.order_line} className="list-row compact-move">
                  <span className="list-main">
                    <span className="list-title">{l.name}</span>
                    <span className="list-sub">
                      {t('dn.left_of', { left: qtyText(l.left), of: qtyText(l.ordered), unit: unitText(l.unit) })}
                    </span>
                  </span>
                  <input
                    className={`input inv-credit-qty${v === null || (v ?? 0) > l.left + 1e-9 ? ' invalid' : ''}`}
                    inputMode="decimal"
                    aria-label={l.name}
                    value={qty[l.order_line] ?? ''}
                    onChange={(e) => setQty({ ...qty, [l.order_line]: e.target.value })}
                  />
                </div>
              ))}
            </div>
          </section>
        )}
        <div className="dn-fields">
          <Field label={t('quote.date')}>
            <input className="input" type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} />
          </Field>
          <Field label={t('dn.vehicle')} hint={t('dn.vehicle_hint')}>
            <input className="input mono" value={vehicle} maxLength={20} onChange={(e) => setVehicle(e.target.value.toUpperCase())} />
          </Field>
          <Field label={t('dn.address')} hint={t('dn.address_hint')}>
            <input className="input" value={address} maxLength={200} onChange={(e) => setAddress(e.target.value)} />
          </Field>
          <Field label={t('dn.town')} hint={t('dn.town_hint')}>
            <input className="input" value={town} maxLength={80} onChange={(e) => setTown(e.target.value)} />
          </Field>
        </div>
        <fieldset className="dn-owner">
          <legend>{t('dn.whose_vehicle')}</legend>
          <div className="segmented" role="radiogroup" aria-label={t('dn.whose_vehicle')}>
            {(['own', 'carrier'] as const).map((k) => (
              <button key={k} type="button" role="radio" aria-checked={owner === k} className={owner === k ? 'active' : ''} onClick={() => setOwner(k)}>
                {t(`dn.owner_${k}`)}
              </button>
            ))}
          </div>
        </fieldset>
        <div className="dn-fields">
          <Field label={t('dn.carrier')} hint={owner === 'carrier' ? t('dn.carrier_name_hint') : t('dn.carrier_hint')}>
            <input className="input" value={carrier} maxLength={80} onChange={(e) => setCarrier(e.target.value)} />
          </Field>
          {owner === 'carrier' ? (
            <Field label={t('dn.carrier_nipt', { label: ownTaxLabel() })} error={niptBad ? idError(businessRules()?.taxId, carrierId) : null}>
              <input className="input mono" value={carrierId} maxLength={30} onChange={(e) => setCarrierId(e.target.value.toUpperCase())} />
            </Field>
          ) : null}
        </div>
        <Field label={t('inv.note')}>
          <textarea className="input" rows={2} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/** The delivery note on A4: what left, for whom, and room for both signatures. No prices. */
export function DeliveryPaper({ d }: { d: DeliveryDetail }) {
  const n = d.delivery;
  const c = d.company;
  const b = d.customer;
  return (
    <article className="inv-paper" aria-label={deliveryName(n)}>
      <header className="inv-paper-head">
        <div>
          <p className="inv-seller">{c.trade_name || c.legal_name}</p>
          {c.trade_name && c.trade_name !== c.legal_name ? <p>{c.legal_name}</p> : null}
          {ownIdLines(c).map((line) => (
            <p key={line}>{line}</p>
          ))}
          <p>{addressLine(c)}</p>
          <p>{[c.phone, c.email].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="inv-paper-title">
          <h2>{t('dn.p_title')}</h2>
          <p className="inv-number">{n.name ?? t('dn.p_draft')}</p>
          <dl>
            <dt>{t('quote.date')}</dt>
            <dd>{day(n.date)}</dd>
            <dt>{t('dn.order')}</dt>
            <dd>{quoteName({ ...d.order, order: true })}</dd>
            {d.order.reference ? (
              <>
                <dt>{t('inv.reference')}</dt>
                <dd>{d.order.reference}</dd>
              </>
            ) : null}
          </dl>
        </div>
      </header>
      <section className="inv-paper-parties">
        <div>
          <p className="inv-label">{t('dn.p_to')}</p>
          <p className="strong">{b.name}</p>
          {b.tax_id ? <p>{t('inv.p_id', { type: buyerIdLabel(b), id: b.tax_id })}</p> : null}
          <p>{buyerAddress(b)}</p>
          {n.address || n.town ? <p className="strong">{t('dn.p_deliver_to', { address: [n.address, n.town].filter(Boolean).join(', ') })}</p> : null}
        </div>
        <div>
          <p className="inv-label">{t('dn.p_from')}</p>
          <p>{d.branch.name}</p>
          <p>{[d.branch.address, d.branch.city].filter(Boolean).join(', ')}</p>
          {n.carrier || n.vehicle ? <p>{[n.carrier, n.carrier_tax_id ? t('inv.p_tax', { label: ownTaxLabel(), id: n.carrier_tax_id }) : null, n.vehicle].filter(Boolean).join(' · ')}</p> : null}
        </div>
      </section>
      <table className="inv-paper-lines">
        <thead>
          <tr>
            <th>#</th>
            <th>{t('inv.description')}</th>
            <th className="num">{t('dn.p_ordered')}</th>
            <th className="num">{t('dn.p_qty')}</th>
          </tr>
        </thead>
        <tbody>
          {d.lines.map((l, i) => (
            <tr key={l.id}>
              <td>{i + 1}</td>
              <td>{l.name}</td>
              <td className="num">
                {qtyText(l.ordered)} {unitText(l.unit)}
              </td>
              <td className="num strong">
                {qtyText(l.qty)} {unitText(l.unit)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {n.note ? <p>{n.note}</p> : null}
      {d.fiscal ? <PaperWtn f={d.fiscal} /> : null}
      <section className="dn-sign">
        <div>
          <p className="inv-label">{t('dn.p_handed')}</p>
          <span className="dn-sign-line">{n.done_by_name ?? ''}</span>
          <span className="muted">{t('dn.p_sign')}</span>
        </div>
        <div>
          <p className="inv-label">{t('dn.p_received')}</p>
          <span className="dn-sign-line" />
          <span className="muted">{t('dn.p_sign_date')}</span>
        </div>
      </section>
    </article>
  );
}


/** Where the fiscal delivery note stands with the tax office. */
function WtnBox({ f }: { f: Wtn }) {
  const tone = f.state === 'rejected' ? 'danger' : f.state === 'fiscalized' ? 'ok' : 'warn';
  return (
    <div className={`callout callout-${tone} inv-fiscal`}>
      <Icon name={tone === 'ok' ? 'checkCircle' : tone === 'danger' ? 'alert' : 'clock'} size={16} />
      <div className="inv-fiscal-text">
        <strong>{t(f.state === 'rejected' ? 'dn.wtn_rejected' : f.state === 'fiscalized' ? 'dn.wtn_done' : 'dn.wtn_pending')}</strong>
        {f.state === 'pending' ? <span>{t('dn.wtn_pending_body')}</span> : null}
        {f.state === 'rejected' && f.last_error ? <span>{f.last_error}</span> : null}
        {f.state === 'rejected' ? <span>{t('inv.fiscal_fix_hint')}</span> : null}
      </div>
    </div>
  );
}

/** The fiscal codes printed on the delivery note. */
function PaperWtn({ f }: { f: Wtn }) {
  const rows: [MessageKey, string | null][] = [
    ['dn.p_wtn_number', f.wtn_num],
    ['dn.p_wtnic', f.wtnic],
    ['dn.p_fwtnic', f.fwtnic ?? t('inv.p_fic_waiting')],
    ['inv.p_operator', f.operator],
    ['inv.p_business_unit', f.business_unit],
    ['inv.p_software', f.software],
  ];
  return (
    <section className="inv-paper-fiscal dn-paper-fiscal" aria-label={t('inv.p_fiscal')}>
      <div>
        {f.test ? <p className="inv-paper-test">{t('inv.p_fiscal_test')}</p> : null}
        <dl>
          {rows
            .filter(([, v]) => v)
            .map(([k, v]) => (
              <Fragment key={k}>
                <dt>{t(k)}</dt>
                <dd className="mono">{v}</dd>
              </Fragment>
            ))}
        </dl>
      </div>
    </section>
  );
}
